import React from 'react'
import { BottomSheet, Typography } from '@s4l/ds'
import { useS4LContext } from '@s4l/react-context'
import type { Rate } from '@s4l/stayforlong/booking/Rate'
import { IconCheck } from '@s4l/icons/IconCheck'
import { useUseCase } from '@s4l/utils/hooks/useUseCase'
import type { UtilityClassNames } from '@s4l/utility-css'

import styles from '../styles.module.css'

interface IPaymentAndCancellationInfoProps {
  model: Rate
}

function PaymentAndCancellationInfo({ model }: Readonly<IPaymentAndCancellationInfoProps>) {
  const { i18n } = useS4LContext()

  const { data: market, loading, error } = useUseCase('GetCurrentMarketSharedUseCase')

  if (error || loading || !market) return null

  const deposit = model?.cancellationPriceFormatted({ locale: i18n.locale, market }) ?? ''
  const payDate = model?.cancellationDateFormatted({ locale: i18n.locale }) ?? ''

  return (
    <BottomSheet.Content
      title={i18n.formatMessage({ id: 'policies.rate-conditions' })}
      modalOnDesktop
      size="s"
    >
      <div className={styles.bottomSheetContent} data-rate={model.paymentPolicy()}>
        <Typography as="h5" variant="body" size="l" bold color="var(--color--neutral--700)">
          {i18n.formatMessage({ id: 'global.payment' })}
        </Typography>
        {model.isNRF() && (
          <div>
            <ul className={styles.list}>
              <Typography variant="caption" size="l" as="li">
                <IconCheck size={16} />
                {i18n.formatMessage({ id: 'global.pay-now' })}
              </Typography>
            </ul>
            <Typography variant="caption" size="l" as="p">
              {i18n.formatMessage({ id: 'reservation.non-refundable-offer' })}
            </Typography>
          </div>
        )}
        {model.isSFL() && (
          <>
            <ul className={styles.list}>
              <Typography
                variant="caption"
                size="l"
                as="li"
                className={styles.featured as UtilityClassNames}
                bold
              >
                <IconCheck size={16} />
                {i18n.formatMessage({ id: 'room.pay-partial' }, { pay_rate: deposit })}
                {` (${i18n.formatMessage({ id: 'policies.deposit' })})`}
              </Typography>
              <Typography
                variant="caption"
                size="l"
                as="li"
                className={styles.featured as UtilityClassNames}
                bold
              >
                <IconCheck size={16} />
                {i18n.formatMessage({ id: 'policies.pay-the-rest-on' }, { date: payDate })}
              </Typography>
            </ul>
            <Typography variant="caption" size="l">
              {i18n.formatMessage({ id: 'payment.deposit' }, { price: deposit })}
            </Typography>
          </>
        )}
        {model.isNOR() && (
          <>
            <ul className={styles.list}>
              <Typography
                variant="caption"
                size="l"
                as="li"
                className={styles.featured as UtilityClassNames}
              >
                <IconCheck size={16} />
                {i18n.formatMessage({ id: 'policies.pay-on' }, { date: payDate })}
              </Typography>
            </ul>
            <Typography variant="caption" size="l">
              {i18n.formatMessage({ id: 'payment.no-prepayment' })}
            </Typography>
          </>
        )}
        {model.isNORE() && (
          <>
            <ul className={styles.list}>
              <Typography variant="caption" size="l" as="li">
                <IconCheck size={16} />
                {i18n.formatMessage({ id: 'global.pay-now' })}
              </Typography>
            </ul>
            <Typography variant="caption" size="l" as="p">
              {i18n.formatMessage({ id: 'reservation.non-refundable-offer' })}
            </Typography>
          </>
        )}

        <Typography as="h5" variant="body" size="l" bold color="var(--color--neutral--700)">
          {i18n.formatMessage({ id: 'global.cancellation-policies' })}
        </Typography>
        {model.isNRF() && (
          <div>
            <ul className={styles.list}>
              <Typography
                variant="caption"
                size="l"
                as="li"
                className={styles.dot as UtilityClassNames}
              >
                {i18n.formatMessage({ id: 'reservation.no-refundable' })}
              </Typography>
            </ul>
            <Typography variant="caption" size="l" as="p">
              {i18n.formatMessage({ id: 'cancelation.no-refundable' })}
            </Typography>
          </div>
        )}
        {model.isSFL() && (
          <div>
            <ul className={styles.list}>
              <Typography
                variant="caption"
                size="l"
                as="li"
                className={styles.featured as UtilityClassNames}
                bold
              >
                <IconCheck size={16} />
                {i18n.formatMessage({ id: 'policies.cancel-until' }, { date: payDate })}
                <span className={styles.nonFeatured}>
                  ({i18n.formatMessage({ id: 'policies.deposit-non-refundable' })})
                </span>
              </Typography>
            </ul>
            <Typography variant="caption" size="l" as="p">
              {i18n.formatMessage(
                { id: 'payment.cancel-for-free-slf' },
                { date: payDate, amount: deposit }
              )}
            </Typography>
          </div>
        )}
        {(model.isNOR() || model.isNORE()) && (
          <div>
            <ul className={styles.list}>
              <Typography
                variant="caption"
                size="l"
                as="li"
                className={styles.featured as UtilityClassNames}
              >
                <IconCheck size={16} />
                {i18n.formatMessage({ id: 'policies.free-cancellation-before' }, { date: payDate })}
              </Typography>
            </ul>
            <Typography variant="caption" size="l" as="p">
              {i18n.formatMessage({ id: 'payment.cancel-for-free-nor' }, { date: payDate })}
            </Typography>
          </div>
        )}
      </div>
    </BottomSheet.Content>
  )
}

PaymentAndCancellationInfo.displayName = 'RoomConfigurator.Form.Info.PaymentAndCancellationInfo'

export default PaymentAndCancellationInfo
